import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { usePortal } from '../PortalContext';
import { usePortalJobs } from '../hooks/usePortalJobs';
import { PortalRateLimited, useSendPortalMessage } from '../hooks/usePortalActions';
import { jobIsOpen } from '../status';

const MAX_LENGTH = 2000;
const NO_JOB = 'none';

export default function PortalMessage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { company, formatShortDate, primaryColor } = usePortal();
  const jobsQ = usePortalJobs(company.id);
  const send = useSendPortalMessage(company.id);

  const [message, setMessage] = useState('');
  const [jobId, setJobId] = useState<string>(params.get('job') || NO_JOB);

  const jobs = jobsQ.data ?? [];
  const jobOptions = [...jobs.filter((j) => jobIsOpen(j.status)), ...jobs.filter((j) => !jobIsOpen(j.status))];
  const homePath = `/portal/${slug}/home`;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const text = message.trim();
    if (!text) return;
    try {
      await send.mutateAsync({ message: text, jobId: jobId === NO_JOB ? null : jobId });
      toast.success('Message sent. The office will get back to you.');
      navigate(homePath);
    } catch (err) {
      toast.error(err instanceof PortalRateLimited
        ? 'You have sent quite a few messages recently. Please try again later.'
        : 'We could not send your message. Please try again.');
    }
  };

  return (
    <div className="space-y-4">
      <Button asChild variant="ghost" size="sm" className="-ml-2 gap-1.5">
        <Link to={homePath}><ArrowLeft className="h-4 w-4" /> Back to your account</Link>
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>Send us a message</CardTitle>
          <CardDescription>
            Goes straight to the office at {company.trading_name || company.name}. They'll reply by phone or email.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {jobOptions.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="portal-message-job">About a job (optional)</Label>
                <Select value={jobId} onValueChange={setJobId}>
                  <SelectTrigger id="portal-message-job">
                    <SelectValue placeholder="No specific job" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_JOB}>No specific job</SelectItem>
                    {jobOptions.map((j) => (
                      <SelectItem key={j.id} value={j.id}>
                        {j.job_number}
                        {j.service_type_name ? ` · ${j.service_type_name}` : ''}
                        {j.scheduled_date ? ` · ${formatShortDate(j.scheduled_date)}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="portal-message-body">Your message</Label>
              <Textarea
                id="portal-message-body"
                rows={6}
                maxLength={MAX_LENGTH}
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, MAX_LENGTH))}
                placeholder="How can we help?"
                required
              />
              <p className="text-right text-xs text-muted-foreground">{message.length} / {MAX_LENGTH}</p>
            </div>

            <Button
              type="submit"
              size="lg"
              className="w-full gap-2 sm:w-auto"
              style={{ backgroundColor: primaryColor }}
              disabled={send.isPending || !message.trim()}
            >
              {send.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Send message
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
